import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, Popover, Typography, Link } from '@mui/material'
import { HelpOutline as HelpIcon } from '@mui/icons-material'
import { getPageHelpConfig, type PageHelpKey } from '@/config/pageHelp'

export type PageHelpPopoverProps =
  | {
      /** Ключ страницы из конфига (предпочтительно) */
      pageKey: PageHelpKey
      subtitleKey?: never
      docsUrl?: never
      ariaLabel?: string
    }
  | {
      pageKey?: never
      /** Ключ перевода описания (для обратной совместимости) */
      subtitleKey: string
      /** Ссылка на документацию (опционально) */
      docsUrl?: string
      ariaLabel?: string
    }

export function PageHelpPopover(props: PageHelpPopoverProps) {
  const { ariaLabel } = props
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const descriptionKey = 'pageKey' in props && props.pageKey
    ? getPageHelpConfig(props.pageKey).descriptionKey
    : props.subtitleKey
  const docsUrl = 'pageKey' in props && props.pageKey
    ? getPageHelpConfig(props.pageKey).docsUrl
    : (props.docsUrl ?? 'https://docs.easya.ru/')

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-label={ariaLabel ?? t('common.help')}
        color="inherit"
        sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
      >
        <HelpIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 360, p: 2 } } }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(descriptionKey)}
        </Typography>
        <Link
          href={docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          underline="hover"
        >
          {t('common.documentation')}
        </Link>
      </Popover>
    </>
  )
}
